import {AttributeInterface} from "../Interfaces/AttributeInterface";
import {AttributeTypeEnum} from "../enums/AttributeTypeEnum";
import {ValueListInterface} from "../Interfaces/ValueListInterface";
import {AttributeValue} from "./AttributeValue";
import {DataTypesEnum} from "../enums/dataTypesEnum";
import {BusinessObjectBaseInterface} from "../Interfaces/BusinessObjectBaseInterface";
import moment = require("moment");

export class Attribute implements AttributeInterface {
    readonly id: number;
    private _name: string;
    private _description: string;
    private _unitOfMeasure: string;
    private _type: AttributeTypeEnum;
    private _boClass: BusinessObjectBaseInterface;
    private _minLength: number;
    private _maxLength: number;
    private _defaultValue: string | number | boolean;
    static  _defValue: string | number | boolean;
    private _systemRequired: boolean;
    private _required: boolean;
    private _maxDigitsLeftOfDecimal: number;
    private _minDigitsRightOfDecimal: number;
    private _regexValidation: string;
    private _uiSection: string;
    private _prefix: string;
    private _suffix: string;
    private _receiveNotifications: boolean;
    private _createRelationships: boolean;
    private _lookupAttributeId: number | null;
    private _lookupRelTypeId: number | null;
    private _attributeValues: Map<number, ValueListInterface> = new Map();


    constructor(
        id: number, name: string, description: string, unitOfMeasure: string, type: AttributeTypeEnum, minLength: number, maxLength: number,
        maxDigitsLeftOfDecimal: number, minDigitsRightOfDecimal: number, regexValidation: string, uiSection: string, prefix: string, suffix: string, receiveNotifications: boolean,
        boClass: BusinessObjectBaseInterface, attributeValues: ValueListInterface[] | Map<number, ValueListInterface>, required: boolean, systemRequired: boolean,
        defaultValue: string | number | boolean, lookupAttributeId: number | null, createRelationships: boolean, lookupRelTypeId: number | null
    ) {
        this.id = id;
        this._type = type;

        this.name = name;
        this.description = description;
        this.unitOfMeasure = unitOfMeasure;
        this.minLength = minLength;
        this.maxLength = maxLength;
        this.maxDigitsLeftOfDecimal = maxDigitsLeftOfDecimal;
        this.minDigitsRightOfDecimal = minDigitsRightOfDecimal;
        this.regexValidation = regexValidation;
        this.uiSection = uiSection;
        this.boClass = boClass;
        this.defaultValue = defaultValue;
        this.systemRequired = systemRequired;
        this.required = required;
        this.prefix = prefix;
        this.suffix = suffix;
        this.receiveNotifications = receiveNotifications;
        this.setAttributeValues(attributeValues);
        this.lookupRelTypeId = lookupRelTypeId || null;
        this.lookupAttributeId = lookupAttributeId || null;
        this.createRelationships = !!createRelationships;
    }

    get name(): string {
        return this._name;
    }

    get boClass(): BusinessObjectBaseInterface {
        return this._boClass;
    }

    set boClass(value: BusinessObjectBaseInterface) {
        this._boClass = value;
    }

    get systemRequired(): boolean {
        return this._systemRequired;
    }

    get required(): boolean {
        return this._required;
    }

    set required(value: boolean) {
        this._required = value;
    }

    set systemRequired(value: boolean) {
        this._systemRequired = value;
    }

    get defaultValue(): string | number | boolean {
        return this._defaultValue;
    }

    get receiveNotifications(): boolean {
        return this._receiveNotifications;
    }

    set receiveNotifications(value: boolean) {
        this._receiveNotifications = value;
    }

    set defaultValue(value: string | number | boolean) {
        switch (this.type) {
            case AttributeTypeEnum.TEXT:
            case AttributeTypeEnum.JSON:
            case AttributeTypeEnum.TIME:
            case AttributeTypeEnum.DATE:
            case AttributeTypeEnum.EMAIL:
            case AttributeTypeEnum.TIMESTAMP:
            case AttributeTypeEnum.INCREMENTING:
            case AttributeTypeEnum.BOOLEAN:
                this._defaultValue = value;
                break;
            case AttributeTypeEnum.INT:
                this._defaultValue = parseInt((value as string), 10);
                break;
            case AttributeTypeEnum.DECIMAL:
                    let toFixedLength = 0;
                    let str = String(value);

                    [".", ","].forEach(seperator => {
                        let arr = str.split(seperator);
                        if( arr.length === 2 ){
                            toFixedLength = arr[1].length;
                        }
                    });

                    this._defaultValue = parseFloat(str).toFixed(toFixedLength);
                break;
            case AttributeTypeEnum.VALUE_LIST:
                this._defaultValue = parseInt((value as string), 10);
                break;
        }
    }

    set name(value: string) {
        this._name = value;
    }

    get description(): string {
        return this._description;
    }

    set description(value: string) {
        this._description = value;
    }

    get unitOfMeasure(): string {
        return this._unitOfMeasure;
    }

    set unitOfMeasure(value: string) {
        this._unitOfMeasure = value;
    }

    get type(): AttributeTypeEnum {
        return this._type;
    }

    set type(value: AttributeTypeEnum) {
        this._type = value;
        switch (value) {
            case AttributeTypeEnum.TEXT:
                this._minDigitsRightOfDecimal = -1;
                this._maxDigitsLeftOfDecimal = -1;
                this._defaultValue = "n/a";
                Attribute._defValue = "n/a";
                break;
            case AttributeTypeEnum.BOOLEAN:
                this._minDigitsRightOfDecimal = -1;
                this._maxDigitsLeftOfDecimal = -1;
                this._defaultValue = "n/a";
                Attribute._defValue = "n/a";
                break;
            case AttributeTypeEnum.JSON:
                this._minDigitsRightOfDecimal = -1;
                this._maxDigitsLeftOfDecimal = -1;
                this._defaultValue = "{}";
                Attribute._defValue = "{}";
                break;
            case AttributeTypeEnum.INT:
                this._minDigitsRightOfDecimal = -1;
                this._maxDigitsLeftOfDecimal = -1;
                this._defaultValue = -1;
                Attribute._defValue = -1;
                break;
            case AttributeTypeEnum.DECIMAL:
                this._minDigitsRightOfDecimal = -1;
                this._maxDigitsLeftOfDecimal = -1;
                this._defaultValue = -1;
                Attribute._defValue = -1;
                break;
            case AttributeTypeEnum.DATE:
                this._minLength = -1;
                this._maxLength = -1;
                this._minDigitsRightOfDecimal = -1;
                this._maxDigitsLeftOfDecimal = -1;
                this._defaultValue = moment().format("YYYY-MM-DD");
                break;
            case AttributeTypeEnum.TIME:
                this._minLength = -1;
                this._maxLength = -1;
                this._minDigitsRightOfDecimal = -1;
                this._maxDigitsLeftOfDecimal = -1;
                this._defaultValue = moment().format("HH:mm:ss");
                break;
            case AttributeTypeEnum.TIMESTAMP:
                this._minLength = -1;
                this._maxLength = -1;
                this._minDigitsRightOfDecimal = -1;
                this._maxDigitsLeftOfDecimal = -1;
                this._defaultValue = moment().format("YYYY-MM-DDTHH:mm:ss");
                break;
            case AttributeTypeEnum.EMAIL:
                this._minLength = -1;
                this._maxLength = -1;
                this._minDigitsRightOfDecimal = -1;
                this._maxDigitsLeftOfDecimal = -1;
                this._regexValidation = ".*";
                this._defaultValue = "[]";
                break;
            case AttributeTypeEnum.VALUE_LIST:
                this._minLength = -1;
                this._maxLength = -1;
                this._minDigitsRightOfDecimal = -1;
                this._maxDigitsLeftOfDecimal = -1;
                this._regexValidation = ".*";
                this._defaultValue = Array.from(this._attributeValues.values())[0] ? Array.from(this._attributeValues.values())[0].id : null;
                break;
        }
    }

    get minLength(): number {
        return this._minLength;
    }

    set minLength(value: number) {
        if (this.type === AttributeTypeEnum.DECIMAL) {
            this._minLength = parseFloat(value.toString());
        } else {
            this._minLength = parseInt(value.toString(), 10);
        }
    }

    get maxLength(): number {
        return this._maxLength;
    }

    set maxLength(value: number) {
        if (this.type === AttributeTypeEnum.DECIMAL) {
            this._maxLength = parseFloat(value.toString());
        } else {
            this._maxLength = parseInt(value.toString(), 10);
        }
    }

    get maxDigitsLeftOfDecimal(): number {
        return this._maxDigitsLeftOfDecimal;
    }

    set maxDigitsLeftOfDecimal(value: number) {
        if (this.type === AttributeTypeEnum.DECIMAL) {
            this._maxDigitsLeftOfDecimal = parseInt(value.toString(), 10);
        } else {
            this._maxDigitsLeftOfDecimal = -1;
        }
    }

    get minDigitsRightOfDecimal(): number {
        return this._minDigitsRightOfDecimal;
    }

    set minDigitsRightOfDecimal(value: number) {
        if (this.type === AttributeTypeEnum.DECIMAL) {
            this._minDigitsRightOfDecimal = parseInt(value.toString(), 10);
        } else {
            this._minDigitsRightOfDecimal = -1;
        }
    }

    get regexValidation(): string {
        return this._regexValidation;
    }

    set regexValidation(value: string) {
        this._regexValidation = value;
    }

    get uiSection(): string {
        return this._uiSection;
    }

    set uiSection(value: string) {
        this._uiSection = value;
    }


    get createRelationships(): boolean {
        return this._createRelationships;
    }

    set createRelationships(value: boolean) {
        this._createRelationships = value;
    }

    get lookupAttributeId(): number | null {
        return this._lookupAttributeId;
    }

    set lookupAttributeId(value: number | null) {
        this._lookupAttributeId = value;
    }

    get lookupRelTypeId(): number | null {
        return this._lookupRelTypeId;
    }

    set lookupRelTypeId(value: number | null) {
        this._lookupRelTypeId = value;
    }

    get attributeValues(): Map<number, ValueListInterface> {
        return this._attributeValues;
    }

    set attributeValues(value: Map<number, ValueListInterface>) {
        this._attributeValues = value;
    }

    clone(): Attribute {
        return new Attribute(this.id, this.name, this.description, this.unitOfMeasure, this.type, this.minLength, this.maxLength, this.maxDigitsLeftOfDecimal,
            this.minDigitsRightOfDecimal, this.regexValidation, this.uiSection, this.prefix, this.suffix, this.receiveNotifications, this.boClass, this.getAttributeValues(DataTypesEnum.ARRAY),
            this.required, this.systemRequired, this.defaultValue, this.lookupAttributeId, this.createRelationships, this.lookupRelTypeId);
    }

    isErasable(): { erasable: boolean; msg: string | null } {
        if (this.systemRequired) {
            return {erasable: false, msg: "You cannot delete this attribute, because it is set as system attribute."};
        }
        return {erasable: true, msg: null};
    }

    equal(attribute: Attribute): boolean {
        if (!attribute) {
            return false;
        }
        if (this.name.trim() !== attribute.name.trim()) {
            return false;
        }
        if (this.description.trim() !== attribute.description.trim()) {
            return false;
        }
        if (this.unitOfMeasure.trim() !== attribute.unitOfMeasure.trim()) {
            return false;
        }
        if (this.type.trim() !== attribute.type.trim()) {
            return false;
        }
        if (this.minLength !== attribute.minLength) {
            return false;
        }
        if (this.maxLength !== attribute.maxLength) {
            return false;
        }
        if (this.maxDigitsLeftOfDecimal !== attribute.maxDigitsLeftOfDecimal) {
            return false;
        }
        if (this.minDigitsRightOfDecimal !== attribute.minDigitsRightOfDecimal) {
            return false;
        }
        if (this.regexValidation !== attribute.regexValidation) {
            return false;
        }
        if (this.systemRequired !== attribute.systemRequired) {
            return false;
        }
        if (this.defaultValue !== undefined  && attribute.defaultValue !== undefined) {
            if (this.defaultValue.toString() !== attribute.defaultValue.toString()) {
                return false;
            }
        }
        if (this.required !== attribute.required) {
            return false;
        }
        if (this.prefix !== attribute.prefix) {
            return false;
        }
        if (this.suffix !== attribute.suffix) {
            return false;
        }
        // @ts-ignore
        if (this.receiveNotifications !== attribute.receiveNotifications && attribute.receiveNotifications !== '') {
            return false;
        }
        if (this.lookupRelTypeId !== attribute.lookupRelTypeId) {
            return false;
        }
        if (this.lookupAttributeId !== attribute.lookupAttributeId) {
            return false;
        }
        if (this.createRelationships !== attribute.createRelationships) {
            return false;
        }
        return this.uiSection === attribute.uiSection;
    }

    isValid(): { valid: boolean; data: Map<string, { valid: boolean, msg: string }> } {
        const data = new Map([
            ["name", {
                valid: true,
                msg: null
            }],
            ["description", {
                valid: true,
                msg: null
            }],
            ["unitOfMeasure", {
                valid: true,
                msg: null
            }],
            ["type", {
                valid: true,
                msg: null
            }],
            ["minLength", {
                valid: true,
                msg: null
            }],
            ["maxLength", {
                valid: true,
                msg: null
            }],
            ["minDigitsRightOfDecimal", {
                valid: true,
                msg: null
            }],
            ["maxDigitsLeftOfDecimal", {
                valid: true,
                msg: null
            }],
            ["regexValidation", {
                valid: true,
                msg: null
            }],
            ["uiSection", {
                valid: true,
                msg: null
            }],
            ["systemRequired", {
                valid: true,
                msg: null
            }],
            ["defaultValue", {
                valid: true,
                msg: null
            }],
            ["receiveNotifications", {
                valid: true,
                msg: null
            }]
        ]);

        if (this.name === "") {
            data.set("name", {valid: false, msg: `Attribute "Name" can't be an empty string.`});
        }
        if (this.description === "") {
            data.set("description", {valid: false, msg: `Attribute "Description" can't be an empty string.`});
        }
        if (this.unitOfMeasure === "") {
            data.set("unitOfMeasure", {valid: false, msg: `Attribute "Unit Of Measure" can't be an empty string.`});
        }
        if (isNaN(this.minLength)) {
            data.set("minLength", {valid: false, msg: `Attribute "Min Length" must be a number.`});
        }
        if (isNaN(this.maxLength)) {
            data.set("maxLength", {valid: false, msg: `Attribute "Max Length" must be a number.`});
        }
        if (isNaN(this.maxDigitsLeftOfDecimal)) {
            data.set("maxDigitsLeftOfDecimal", {
                valid: false,
                msg: `Attribute "Max Digits Left" must be a number.`
            });
        }
        if (isNaN(this.minDigitsRightOfDecimal)) {
            data.set("minDigitsRightOfDecimal", {
                valid: false,
                msg: `Attribute "Min Digits Right" must be a number.`
            });
        }
        if (this.defaultValue === "") {
            data.set("defaultValue", {valid: false, msg: `Attribute "Default Value" cannot be empty.`});
        }
        if (this.regexValidation === "") {
            if (!Attribute.isValidRegex(this.regexValidation.trim())) {
                data.set("regexValidation", {
                    valid: false,
                    msg: `Attribute "Regex Validation" is invalid.`
                });
            }
        }

        switch (this.type) {
            case AttributeTypeEnum.JSON:
            case AttributeTypeEnum.TEXT: {
                if (data.get("minLength").valid && this.minLength < -1) {
                    data.set("minLength", {valid: false, msg: `Invalid Min Length.`})
                }

                if (data.get("maxLength").valid && this.maxLength < -1) {
                    data.set("maxLength", {valid: false, msg: "Invalid Max Length."})
                }

                if ((data.get("maxLength").valid && data.get("minLength").valid) && (this.minLength > this.maxLength && this.maxLength !== -1)) {
                    data.set("minLength", {valid: false, msg: "Min Length must be less than Max Length."})
                }

                if (data.get("defaultValue").valid && (this.minLength !== -1 && (this.defaultValue as string).length < this.minLength)) {
                    data.set("defaultValue", {
                        valid: false,
                        msg: "The Default Value length must be greater than or equal to Min Length."
                    });
                }

                if (data.get("defaultValue").valid && (this.maxLength !== -1 && (this.defaultValue as string).length > this.maxLength)) {
                    data.set("defaultValue", {
                        valid: false,
                        msg: "The Default Value length must be less than or equal to Max Length."
                    });
                }

                if (this.type === AttributeTypeEnum.JSON) {
                    try {
                        JSON.parse((this.defaultValue as string))
                    } catch (e) {
                        data.set("defaultValue", {
                            valid: false,
                            msg: "The default value is not valid JSON string."
                        });
                    }
                }
            }
                break;
            case AttributeTypeEnum.DECIMAL:
            case AttributeTypeEnum.INT: {
                if (data.get("minLength").valid && (this.minLength < -1 && this.maxLength < -1 && this.minLength > this.maxLength)) {
                    data.set("minLength", {
                        valid: false,
                        msg: "Min Value must be less than Max Value."
                    })
                }

                if (data.get("minLength").valid && (this.minLength > -1 && this.maxLength < -1)) {
                    data.set("minLength", {
                        valid: false,
                        msg: "Min Value must be less than  Max Value."
                    })
                }

                if (data.get("minLength").valid && (this.minLength > -1 && this.maxLength > -1 && this.minLength > this.maxLength)) {
                    data.set("minLength", {
                        valid: false,
                        msg: "Min Value must be less than  Max Value."
                    })
                }

                if (data.get("defaultValue").valid && isNaN((this.defaultValue as number))) {
                    data.set("defaultValue", {valid: false, msg: "The default value cannot be NaN."});
                }
                if (data.get("defaultValue").valid && (this.minLength !== -1 && this.defaultValue < this.minLength)) {
                    data.set("defaultValue", {
                        valid: false,
                        msg: `The default value must be greater than or equal to ${this.minLength}.`
                    });
                }
                if (data.get("defaultValue").valid && (this.maxLength !== -1 && this.defaultValue > this.maxLength)) {
                    data.set("defaultValue", {
                        valid: false,
                        msg: `The default value must be less than or equal to ${this.maxLength}.`
                    });
                }

                if (this.type === AttributeTypeEnum.DECIMAL) {
                    if (data.get("maxDigitsLeftOfDecimal").valid && this.maxDigitsLeftOfDecimal < -1) {
                        data.set("maxDigitsLeftOfDecimal", {
                            valid: false,
                            msg: "Invalid Max Digits Left."
                        })
                    }
                    if (data.get("minDigitsRightOfDecimal").valid && this.minDigitsRightOfDecimal < -1) {
                        data.set("minDigitsRightOfDecimal", {
                            valid: false,
                            msg: "Invalid Min Digits Right."
                        })
                    }

                    const decArray = this.defaultValue.toString().split(".");
                    if (data.get("defaultValue").valid && this.maxDigitsLeftOfDecimal !== -1) {
                        if (decArray[0].length > this.maxDigitsLeftOfDecimal) {
                            data.set("defaultValue", {
                                valid: false,
                                msg: `The max digits left of decimal point must be less than or equal to ${this.maxDigitsLeftOfDecimal}`
                            });
                        }
                    }

                    if (data.get("defaultValue").valid && this.minDigitsRightOfDecimal !== -1) {
                        if (!decArray[1] || decArray[1].length < this.minDigitsRightOfDecimal) {
                            data.set("defaultValue", {
                                valid: false,
                                msg: `The min digits right of decimal point must be greater than or equal to ${this.minDigitsRightOfDecimal}`
                            });
                        }
                    }
                }
            }
                break;
            case AttributeTypeEnum.TIME:
                if (data.get("defaultValue").valid) {
                    if (!(this.defaultValue as string).toString().match(/(?:[01]\d|2[0-3]):(?:[0-5]\d):(?:[0-5]\d)/g)) {
                        data.set("defaultValue", {
                            valid: false,
                            msg: `Invalid default value format.`
                        });
                    }
                }
                break;
            case AttributeTypeEnum.DATE:
                if (data.get("defaultValue").valid) {
                    if (!(this.defaultValue as string).toString().match(/(\d{4})-(\d{2})-(\d{2})/g)) {
                        data.set("defaultValue", {
                            valid: false,
                            msg: `Invalid default value format.`
                        });
                    }
                }
                break;
            case AttributeTypeEnum.TIMESTAMP:
                if (data.get("defaultValue").valid) {
                    // @ts-ignore
                    if (moment(this.defaultValue).creationData().format !== "YYYY-MM-DDTHH:mm:ss") {
                        data.set("defaultValue", {
                            valid: false,
                            msg: `Invalid default value format.`
                        });
                    }
                }
                break;
            case AttributeTypeEnum.VALUE_LIST:
                if (data.get("defaultValue").valid && isNaN(this.defaultValue as number)) {
                    data.set("defaultValue", {
                        valid: false,
                        msg: `Invalid default value.`
                    });
                }
                break;
            case AttributeTypeEnum.EMAIL:
                try {
                    JSON.parse(<string>this.defaultValue);
                } catch(e) {
                    data.set("defaultValue", {
                        valid: false,
                        msg: `Invalid default value. Invalid JSON provided.`
                    });
                }
                break;
        }
        return {valid: !Array.from(data.values()).some(item => item.valid === false), data}
    }

    static isValidRegex(str): boolean {
        try {
            new RegExp(str);
        } catch (e) {
            return false;
        }
        return true;
    }

    getAttributeValues(arg: string): ValueListInterface[] | Map<number, ValueListInterface> {
        switch (arg) {
            case DataTypesEnum.ARRAY: {
                return Array.from(this._attributeValues.values());
            }
            case DataTypesEnum.MAP: {
                return this._attributeValues;
            }
        }
    }

    setAttributeValues(values: ValueListInterface[] | Map<number, ValueListInterface>): void {
        if (values instanceof Array) {
            values.sort((a, b) => a.name.localeCompare(b.name)).forEach(value => {
                this._attributeValues.set(value.id, new AttributeValue(value.id, value.name, value.description, value.systemRequired))
            })
        } else if (values instanceof Map) {
            this._attributeValues = values;
        }
    }

    getAttributeValue(arg: number | string): ValueListInterface {
        switch (typeof arg) {
            case "number": {
                return this._attributeValues.get(arg);
            }
            case "string": {
                const valueListArray = Array.from(this._attributeValues.values());
                for (let i = 0; i < valueListArray.length; i++) {
                    if (valueListArray[i].name.trim().toLowerCase() === arg.trim().toLowerCase()) {
                        return valueListArray[i];
                    }
                }
            }
        }
    }

    hasAttributeValue(arg: number | string): boolean {
        switch (typeof arg) {
            case "number": {
                return this._attributeValues.has(arg);
            }
            case "string": {
                const attributeValues = <ValueListInterface[]>this.getAttributeValues(DataTypesEnum.ARRAY);
                for (let i = 0; i < attributeValues.length; i++) {
                    if (attributeValues[i].name.toLowerCase() === arg.toLowerCase()) {
                        return true;
                    }
                }
            }
        }
        return false
    }

    setAttributeValue(value: ValueListInterface): void {
        this._attributeValues.set(value.id, new AttributeValue(value.id, value.name, value.description, value.systemRequired));
    }

    deleteValueListItem(id: number): void {
        this._attributeValues.delete(id);
    }


    get prefix(): string {
        return this._prefix;
    }

    set prefix(value: string) {
        this._prefix = value;
    }

    get suffix(): string {
        return this._suffix;
    }

    set suffix(value: string) {
        this._suffix = value;
    }

    hasAttributeValueByNameExceptId(name: string, id: number): boolean {
        const attributeValues = <ValueListInterface[]>this.getAttributeValues(DataTypesEnum.ARRAY);
        for (let i = 0; i < attributeValues.length; i++) {
            if (attributeValues[i].name.trim().toLowerCase() === name.trim().toLowerCase() && attributeValues[i].id !== id) {
                return true;
            }
        }
        return false;
    }

    checkAttributeValueBeforeSave(attributeValue: AttributeValue): { valid: boolean; data: Map<string, { valid: boolean, msg: string }> } {
        const result = attributeValue.isValid();

        if (this.hasAttributeValue(attributeValue.id)) {
            const attrVal = this.getAttributeValue(attributeValue.id);
            const equal = attrVal.equal(attributeValue);
            result.data.set("equal", {
                valid: !equal,
                msg: equal ? "There are no changes." : null
            })
        }
        if (result.data.get("name").valid) {
            const exists = this.hasAttributeValueByNameExceptId(attributeValue.name, attributeValue.id);
            result.data.set("name", {
                valid: !exists,
                msg: exists ? `Attribute value with name: ${attributeValue.name} already exists.` : null
            });
        }
        result.valid = !Array.from(result.data.values()).some(item => item.valid === false);
        return result
    }

    update(attribute: AttributeInterface): Attribute {
        this.name = attribute.name;
        this._type = attribute.type;
        this.description = attribute.description;
        this.unitOfMeasure = attribute.unitOfMeasure;
        this.minLength = attribute.minLength;
        this.maxLength = attribute.maxLength;
        this.maxDigitsLeftOfDecimal = attribute.maxDigitsLeftOfDecimal;
        this.minDigitsRightOfDecimal = attribute.minDigitsRightOfDecimal;
        this.regexValidation = attribute.regexValidation;
        this.uiSection = attribute.uiSection;
        this.required = attribute.required;
        this.systemRequired = attribute.systemRequired;
        this.prefix = attribute.prefix;
        this.suffix = attribute.suffix;
        this.receiveNotifications = attribute.receiveNotifications;
        this.lookupAttributeId = attribute.lookupAttributeId;
        this.lookupRelTypeId = attribute.lookupRelTypeId;
        this.createRelationships = attribute.createRelationships;
        this.defaultValue = attribute.defaultValue;
        this.setAttributeValues(attribute.attributeValues);
        return this;
    }
}



// WEBPACK FOOTER //
// ./app/components/bo-core/models/Attribute.ts